import { tr } from '@/i18n/tr';
import type { ProduceStatus } from '@/types/domain';

/**
 * The single place that turns a stored produce status into the word the user
 * sees. Every surface calls this instead of reading tr.listing.produceStatus
 * directly, so the category-dependent wording can never be right on one screen
 * and stale on another.
 *
 * Only the packaged option varies. "Dalında" is constant, there are always
 * exactly two options, and the stored values ('onBranch' / 'inCrate') never
 * change — the whole feature is a lookup, not a data model.
 */
export function produceStatusLabel(
  status: ProduceStatus,
  /** Category NAME, when the surface knows it. Unknown → the default wording. */
  categoryName?: string
): string {
  if (status === 'onBranch') return tr.listing.produceStatus.onBranch;
  if (categoryName && tr.listing.packagingByCategory[categoryName]) {
    return tr.listing.packagingByCategory[categoryName];
  }
  return tr.listing.produceStatus.inCrate;
}

/**
 * Label for the packaged option where no category is in scope — the Keşfet
 * filter applies across all of them at once.
 *
 * Derived from the same table rather than hardcoded, so adding a category to
 * `packagingByCategory` updates the filter automatically instead of leaving a
 * second place to remember. Deduped and ordered with the default first.
 */
export function packagedFilterLabel(): string {
  const words = [
    tr.listing.produceStatus.inCrate,
    ...Object.values(tr.listing.packagingByCategory),
  ];
  return [...new Set(words)].join(' / ');
}
